import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

interface DateTimePickerProps {
  value: string;
  onChange: (value: string) => void;
  timezone: string;
  placeholder?: string;
  hasError?: boolean;
  minDateTime?: string;
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function DateTimePicker({ value, onChange, timezone, placeholder = 'Select date & time', hasError = false, minDateTime }: DateTimePickerProps) {
  const minDate = minDateTime ? new Date(minDateTime) : null;
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<'date' | 'time'>('date');
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const hourListRef = useRef<HTMLDivElement>(null);
  const [popoverPosition, setPopoverPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  // Parse current value or use defaults
  const currentDate = value ? new Date(value) : null;
  const [viewMonth, setViewMonth] = useState(currentDate?.getMonth() ?? new Date().getMonth());
  const [viewYear, setViewYear] = useState(currentDate?.getFullYear() ?? new Date().getFullYear());
  const [selectedDate, setSelectedDate] = useState<Date | null>(currentDate);
  const [hour, setHour] = useState(currentDate?.getHours() ?? 19);
  const [minute, setMinute] = useState(currentDate?.getMinutes() ?? 0);

  const updatePopoverPosition = useCallback(() => {
    if (!containerRef.current) return;

    const viewportPadding = 16;
    const popoverGap = 8;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const triggerRect = containerRef.current.getBoundingClientRect();
    const popoverWidth = Math.min(320, viewportWidth - viewportPadding * 2);
    const popoverHeight = popoverRef.current?.offsetHeight ?? 420;
    const spaceBelow = viewportHeight - triggerRect.bottom - popoverGap - viewportPadding;
    const spaceAbove = triggerRect.top - popoverGap - viewportPadding;

    let top = triggerRect.bottom + popoverGap;
    if (popoverHeight > spaceBelow && spaceAbove > spaceBelow) {
      top = Math.max(viewportPadding, triggerRect.top - popoverHeight - popoverGap);
    } else if (popoverHeight > spaceBelow) {
      top = Math.max(viewportPadding, viewportHeight - popoverHeight - viewportPadding);
    }

    setPopoverPosition({
      top,
      left: Math.min(
        Math.max(viewportPadding, triggerRect.left),
        viewportWidth - popoverWidth - viewportPadding
      ),
      width: popoverWidth,
    });
  }, []);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const isInsideTrigger = containerRef.current?.contains(target);
      const isInsidePopover = popoverRef.current?.contains(target);

      if (!isInsideTrigger && !isInsidePopover) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setPopoverPosition(null);
      return;
    }

    const animationFrame = window.requestAnimationFrame(updatePopoverPosition);
    window.addEventListener('resize', updatePopoverPosition);
    window.addEventListener('scroll', updatePopoverPosition, true);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener('resize', updatePopoverPosition);
      window.removeEventListener('scroll', updatePopoverPosition, true);
    };
  }, [isOpen, updatePopoverPosition]);

  useEffect(() => {
    if (!isOpen) return;
    const animationFrame = window.requestAnimationFrame(updatePopoverPosition);
    return () => window.cancelAnimationFrame(animationFrame);
  }, [isOpen, view, updatePopoverPosition]);

  // Scroll hour list to show selected hour when time view opens
  useEffect(() => {
    if (view === 'time' && hourListRef.current) {
      const selectedButton = hourListRef.current.querySelector(`[data-hour="${hour}"]`);
      if (selectedButton) {
        selectedButton.scrollIntoView({ block: 'center', behavior: 'instant' });
      }
    }
  }, [view, hour]);

  // Build calendar grid
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const daysInPrevMonth = new Date(viewYear, viewMonth, 0).getDate();

  const calendarDays: { day: number; current: boolean; date: Date }[] = [];

  // Previous month days
  for (let i = firstDay - 1; i >= 0; i--) {
    const d = daysInPrevMonth - i;
    calendarDays.push({
      day: d,
      current: false,
      date: new Date(viewYear, viewMonth - 1, d)
    });
  }

  // Current month days
  for (let d = 1; d <= daysInMonth; d++) {
    calendarDays.push({
      day: d,
      current: true,
      date: new Date(viewYear, viewMonth, d)
    });
  }

  // Next month days to fill grid
  const remaining = 42 - calendarDays.length;
  for (let d = 1; d <= remaining; d++) {
    calendarDays.push({
      day: d,
      current: false,
      date: new Date(viewYear, viewMonth + 1, d)
    });
  }

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setViewMonth(date.getMonth());
    setViewYear(date.getFullYear());

    // If selecting today, ensure time is not in the past
    const now = new Date();
    if (date.toDateString() === now.toDateString()) {
      if (hour < now.getHours()) {
        setHour(now.getHours());
        setMinute(0);
      } else if (hour === now.getHours() && minute < now.getMinutes()) {
        // Round up to next 15-minute interval
        const nextMinute = Math.ceil(now.getMinutes() / 15) * 15;
        if (nextMinute >= 60) {
          setHour(now.getHours() + 1);
          setMinute(0);
        } else {
          setMinute(nextMinute);
        }
      }
    }

    setView('time');
  };

  const handleConfirm = () => {
    if (selectedDate) {
      const finalDate = new Date(selectedDate);
      finalDate.setHours(hour, minute, 0, 0);
      // Format as datetime-local value (YYYY-MM-DDTHH:MM) in local time
      const year = finalDate.getFullYear();
      const month = String(finalDate.getMonth() + 1).padStart(2, '0');
      const day = String(finalDate.getDate()).padStart(2, '0');
      const hours = String(finalDate.getHours()).padStart(2, '0');
      const mins = String(finalDate.getMinutes()).padStart(2, '0');
      const formatted = `${year}-${month}-${day}T${hours}:${mins}`;
      onChange(formatted);
    }
    setIsOpen(false);
  };

  const handleClear = () => {
    setSelectedDate(null);
    onChange('');
    setIsOpen(false);
  };

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };

  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date: Date) => {
    return selectedDate?.toDateString() === date.toDateString();
  };

  const isPastDate = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);

    // Check against minDateTime if provided
    if (minDate) {
      const minDateOnly = new Date(minDate);
      minDateOnly.setHours(0, 0, 0, 0);
      if (checkDate < minDateOnly) return true;
    }

    return checkDate < today;
  };

  const isPastTime = (h: number) => {
    if (!selectedDate) return false;

    // Check against minDateTime if provided
    if (minDate) {
      const selectedDateOnly = new Date(selectedDate);
      selectedDateOnly.setHours(0, 0, 0, 0);
      const minDateOnly = new Date(minDate);
      minDateOnly.setHours(0, 0, 0, 0);

      // If selected date is same as min date, block hours at or before min hour
      if (selectedDateOnly.getTime() === minDateOnly.getTime()) {
        return h <= minDate.getHours();
      }
      // If selected date is after min date, no hour restrictions from minDateTime
      return false;
    }

    const now = new Date();
    // Only restrict time if selected date is today
    if (selectedDate.toDateString() !== now.toDateString()) return false;
    return h < now.getHours();
  };

  const isPastMinute = (m: number) => {
    if (!selectedDate) return false;

    // Check against minDateTime if provided
    if (minDate) {
      const selectedDateOnly = new Date(selectedDate);
      selectedDateOnly.setHours(0, 0, 0, 0);
      const minDateOnly = new Date(minDate);
      minDateOnly.setHours(0, 0, 0, 0);

      // If selected date is same as min date
      if (selectedDateOnly.getTime() === minDateOnly.getTime()) {
        // If hour is before min hour, all minutes blocked (handled by isPastTime)
        if (hour < minDate.getHours()) return true;
        // If hour equals min hour, block minutes at or before min minute
        if (hour === minDate.getHours()) return m <= minDate.getMinutes();
        // If hour is exactly one more than min hour, block minutes that would be less than 15 min after
        if (hour === minDate.getHours() + 1) {
          const minMinute = minDate.getMinutes();
          // e.g., if min is 7:45, and we're at 8:xx, we need m > 0 to be at least 15 min after
          // Actually, we need (hour * 60 + m) - (minHour * 60 + minMinute) > 0
          // Since hour = minHour + 1, we need 60 + m - minMinute > 0, i.e., m > minMinute - 60
          // That's always true for m >= 0 when minMinute < 60
          return false;
        }
      }
      return false;
    }

    const now = new Date();
    // Only restrict if selected date is today and hour is current hour
    if (selectedDate.toDateString() !== now.toDateString()) return false;
    if (hour > now.getHours()) return false;
    if (hour < now.getHours()) return true;
    return m < now.getMinutes();
  };

  const formatDisplayValue = () => {
    if (!value) return placeholder;
    const date = new Date(value);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }) + ' · ' + date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatHour = (h: number) => {
    const period = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12} ${period}`;
  };

  const popover = (
    <div
      ref={popoverRef}
      className="fixed z-[100] bg-panel border border-line rounded-xl shadow-xl overflow-hidden max-w-80"
      style={{
        top: popoverPosition?.top ?? 0,
        left: popoverPosition?.left ?? 0,
        width: popoverPosition?.width ?? 320,
        visibility: popoverPosition ? 'visible' : 'hidden',
      }}
    >
      {/* Header */}
      <div className="flex border-b border-line">
        <button
          type="button"
          onClick={() => setView('date')}
          className={`type-body-sm flex-1 py-3 transition-colors ${
            view === 'date'
              ? 'text-accent border-b-2 border-accent'
              : 'text-text-dim hover:text-text'
          }`}
        >
          Date
        </button>
        <button
          type="button"
          onClick={() => setView('time')}
          className={`type-body-sm flex-1 py-3 transition-colors ${
            view === 'time'
              ? 'text-accent border-b-2 border-accent'
              : 'text-text-dim hover:text-text'
          }`}
        >
          Time
        </button>
      </div>

      {view === 'date' && (
        <div className="p-4">
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={prevMonth}
              className="p-1 text-text-dim hover:text-text transition-colors"
            >
              ←
            </button>
            <span className="type-body-sm text-text">
              {MONTHS[viewMonth]} {viewYear}
            </span>
            <button
              type="button"
              onClick={nextMonth}
              className="p-1 text-text-dim hover:text-text transition-colors"
            >
              →
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {DAYS.map(day => (
              <div key={day} className="type-caption text-center text-text-faint py-1">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((item, i) => {
              const past = isPastDate(item.date);
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => !past && handleDateSelect(item.date)}
                  disabled={past}
                  className={`
                    type-body-sm h-9 rounded-lg transition-colors
                    ${past ? 'text-text-faint/40 cursor-not-allowed' : ''}
                    ${!item.current && !past ? 'text-text-faint' : ''}
                    ${item.current && !past ? 'text-text' : ''}
                    ${isToday(item.date) && !isSelected(item.date) && !past ? 'border border-accent/50' : ''}
                    ${isSelected(item.date)
                      ? 'bg-accent text-white'
                      : !past ? 'hover:bg-bg-secondary' : ''
                    }
                  `}
                >
                  {item.day}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {view === 'time' && (
        <div className="p-4">
          <div className="text-center mb-4">
            <span className="type-body-sm text-text-dim">
              {selectedDate
                ? selectedDate.toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric'
                  })
                : 'Select a date first'
              }
            </span>
          </div>

          <div className="flex gap-4 justify-center mb-4">
            {/* Hour selector */}
            <div className="flex flex-col items-center">
              <label className="type-caption text-text-faint mb-2">Hour</label>
              <div ref={hourListRef} className="bg-bg-secondary rounded-lg p-1 max-h-40 overflow-auto">
                {Array.from({ length: 24 }, (_, i) => {
                  const past = isPastTime(i);
                  return (
                    <button
                      key={i}
                      type="button"
                      data-hour={i}
                      onClick={() => !past && setHour(i)}
                      disabled={past}
                      className={`
                        type-body-sm block w-20 py-1.5 rounded transition-colors
                        ${past ? 'text-text-faint/40 cursor-not-allowed' : ''}
                        ${hour === i
                          ? 'bg-accent text-white'
                          : !past ? 'text-text hover:bg-panel-secondary' : ''
                        }
                      `}
                    >
                      {formatHour(i)}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Minute selector */}
            <div className="flex flex-col items-center">
              <label className="type-caption text-text-faint mb-2">Minute</label>
              <div className="bg-bg-secondary rounded-lg p-1 max-h-40 overflow-auto">
                {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map(m => {
                  const past = isPastMinute(m);
                  return (
                    <button
                      key={m}
                      type="button"
                      onClick={() => !past && setMinute(m)}
                      disabled={past}
                      className={`
                        type-body-sm block w-16 py-1.5 rounded transition-colors
                        ${past ? 'text-text-faint/40 cursor-not-allowed' : ''}
                        ${minute === m
                          ? 'bg-accent text-white'
                          : !past ? 'text-text hover:bg-panel-secondary' : ''
                        }
                      `}
                    >
                      :{m.toString().padStart(2, '0')}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="type-caption text-center text-text-faint">
            {timezone}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex gap-2 p-3 border-t border-line bg-bg-secondary">
        <button
          type="button"
          onClick={handleClear}
          className="btn btn-sm flex-1"
        >
          Clear
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!selectedDate}
          className="btn btn-accent btn-sm flex-1 disabled:opacity-50"
        >
          Confirm
        </button>
      </div>
    </div>
  );

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`input text-left flex items-center gap-3 ${hasError ? 'border-warn' : ''}`}
      >
        <span className="text-text-faint flex-shrink-0">📅</span>
        <span className={`${value ? 'text-text' : 'text-text-faint'} min-w-0 truncate`}>
          {formatDisplayValue()}
        </span>
      </button>

      {isOpen && createPortal(popover, document.body)}
    </div>
  );
}
